import { useCallback, useEffect, useState } from "react";
import platformApi from "@/lib/platformApi";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Languages, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";

const STATUS_STYLE = {
  ready: "bg-teal-50 text-teal-700 border-teal-200",
  stale: "bg-amber-50 text-amber-700 border-amber-200",
  generating: "bg-blue-50 text-blue-700 border-blue-200",
  error: "bg-red-50 text-red-700 border-red-200",
  missing: "bg-slate-50 text-slate-500 border-slate-200",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB",
  { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function TranslationsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    platformApi.get("/i18n/status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000); // reflect background generation
    return () => clearInterval(iv);
  }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data: res } = await platformApi.post("/i18n/regenerate");
      if (res.started.length) toast.success(`Regenerating: ${res.started.join(", ")}`);
      else toast.info("All translations already match the current base catalog");
      load();
    } catch (e) {
      toast.error("Regeneration failed to start");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Skeleton className="h-64 w-full rounded-none" />;

  return (
    <div className="bg-white border border-slate-200" data-testid="translations-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
            <Languages className="h-4 w-4" style={{ color: "var(--brand-accent-ink)" }} />
            UI translations
          </h3>
          <p className="text-[11px] text-slate-400 mt-1">
            Base catalog hash <span className="font-mono">{data.base_hash}</span> · LLM-generated once,
            served from cache. Regeneration only runs for languages out of sync with the base catalog.
          </p>
        </div>
        <Button onClick={regenerate} disabled={busy} data-testid="i18n-regenerate-btn"
          className="rounded-none h-9 text-[12px] bg-[#1E293B] hover:bg-slate-700 text-white">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Regenerate translations
        </Button>
      </div>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-slate-100 text-[10px] uppercase tracking-[0.12em] text-slate-400">
            <th className="text-start px-5 py-2.5 font-semibold">Language</th>
            <th className="text-start px-3 py-2.5 font-semibold">Status</th>
            <th className="text-start px-3 py-2.5 font-semibold">Keys</th>
            <th className="text-start px-3 py-2.5 font-semibold">English fallbacks</th>
            <th className="text-start px-3 py-2.5 font-semibold">Last generated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {data.languages.map((l) => (
            <tr key={l.code} data-testid={`i18n-row-${l.code}`}>
              <td className="px-5 py-3">
                <span className="font-semibold text-[#1E293B]">{l.native}</span>
                <span className="text-slate-400 ms-2 font-mono text-[11px]">{l.code}</span>
                {l.rtl && <span className="ms-2 text-[9px] uppercase border border-slate-200 px-1 py-0.5 text-slate-400">RTL</span>}
              </td>
              <td className="px-3 py-3">
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border px-2 py-0.5 ${STATUS_STYLE[l.status] || STATUS_STYLE.missing}`}
                  data-testid={`i18n-status-${l.code}`}>
                  {l.status === "ready" && <CheckCircle2 className="h-3 w-3" />}
                  {l.status === "generating" && <Loader2 className="h-3 w-3 animate-spin" />}
                  {l.status === "error" && <AlertTriangle className="h-3 w-3" />}
                  {l.status === "stale" && <Clock className="h-3 w-3" />}
                  {l.status}
                </span>
              </td>
              <td className="px-3 py-3 font-mono">{l.key_count ?? "—"}</td>
              <td className="px-3 py-3 font-mono">{l.code === "en" ? "—" : l.fallback_count ?? 0}</td>
              <td className="px-3 py-3 text-slate-500">{l.code === "en" ? "bundled" : fmt(l.generated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-5 py-3 border-t border-slate-100 text-[11px] text-slate-400">
        PDFs (manuals, brochure, payslips, receipts) remain English in this cycle — PDF localization is a future cycle.
      </p>
    </div>
  );
}
